import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Package, ChevronLeft, Plus, Edit2, Trash2, RefreshCw, 
  DollarSign, Clock, Building2, Check, X, Search, Tag,
  AlertCircle, Info, Eye, Copy, ToggleLeft, ToggleRight
} from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const PRODUCT_TYPES = [
  { value: "hourly_parking", label: "Hourly Parking", color: "blue", description: "Pay-per-hour parking" },
  { value: "resident_pass", label: "Resident Pass", color: "purple", description: "Residential parking permits" },
  { value: "staff_pass", label: "Staff Pass", color: "orange", description: "Employee parking passes" },
  { value: "season_ticket", label: "Season Ticket", color: "green", description: "Long-term parking subscriptions" }
];

const VALIDITY_UNITS = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" }
];

const getTypeConfig = (type) => {
  return PRODUCT_TYPES.find(pt => pt.value === type) || { color: "gray", label: type };
};

const ProductsContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite, sites } = useSite();
  
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [tariffs, setTariffs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    product_type: "hourly_parking",
    name: "",
    description: "",
    base_price_pence: 200,
    vat_rate: "",
    site_id: "",
    validity_unit: "hours",
    validity_value: 1,
    active: true
  });

  // Get initial site from URL params
  useEffect(() => {
    const siteFromUrl = searchParams.get('site_id');
    if (siteFromUrl && siteFromUrl !== selectedSite) {
      // Site selector will handle this
    }
  }, [searchParams, selectedSite]);

  useEffect(() => { loadData(); }, [selectedSite, typeFilter, activeTab, showInactive]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "products") {
        const params = new URLSearchParams();
        if (selectedSite !== "all") params.set("site_id", selectedSite);
        if (typeFilter) params.set("product_type", typeFilter);
        if (!showInactive) params.set("active", "true");
        
        const res = await axios.get(`${API}/commercial/products?${params}`, { withCredentials: true });
        setProducts(res.data.products || []);
      } else if (activeTab === "tariffs" && selectedSite !== "all") {
        const res = await axios.get(`${API}/commercial/products/tariffs/${selectedSite}`, { withCredentials: true });
        setTariffs(res.data);
      }
    } catch (err) {
      console.error("Load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      product_type: "hourly_parking",
      name: "",
      description: "",
      base_price_pence: 200,
      vat_rate: "",
      site_id: selectedSite !== "all" ? selectedSite : "",
      validity_unit: "hours",
      validity_value: 1,
      active: true
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      product_type: product.product_type,
      name: product.name,
      description: product.description || "",
      base_price_pence: product.base_price_pence,
      vat_rate: product.vat_rate ?? "",
      site_id: product.site_id || "",
      validity_unit: product.validity_unit || "hours",
      validity_value: product.validity_value || 1,
      active: product.active
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      alert("Product name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        vat_rate: formData.vat_rate === "" ? null : parseFloat(formData.vat_rate),
        site_id: formData.site_id || null
      };

      if (editingProduct) {
        await axios.put(`${API}/commercial/products/${editingProduct.product_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/commercial/products`, payload, { withCredentials: true });
      }
      
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save product: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm("Are you sure you want to delete this product? This action cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/commercial/products/${productId}`, { withCredentials: true });
      loadData();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete product");
    }
  };

  const toggleProductActive = async (product) => {
    try {
      await axios.put(`${API}/commercial/products/${product.product_id}`, {
        active: !product.active
      }, { withCredentials: true });
      loadData();
    } catch (err) {
      console.error("Toggle error:", err);
      alert("Failed to update product status");
    }
  };

  const duplicateProduct = (product) => {
    setEditingProduct(null);
    setFormData({
      product_type: product.product_type,
      name: `${product.name} (Copy)`,
      description: product.description || "",
      base_price_pence: product.base_price_pence,
      vat_rate: product.vat_rate ?? "",
      site_id: product.site_id || "",
      validity_unit: product.validity_unit || "hours",
      validity_value: product.validity_value || 1,
      active: true
    });
    setShowModal(true);
  };

  const seedDefaults = async () => {
    if (!confirm("This will create default products. Continue?")) return;
    try {
      const res = await axios.post(`${API}/commercial/products/seed-defaults`, {}, { withCredentials: true });
      alert(res.data.message || `Created ${res.data.count} products`);
      loadData();
    } catch (err) {
      console.error("Seed error:", err);
      alert("Failed to seed products: " + (err.response?.data?.detail || err.message));
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchTerm || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.product_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = showInactive || p.active !== false;
    return matchesSearch && matchesActive;
  });

  // Group products by type for better organization
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const type = product.product_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(product);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-products-tariffs">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Products & Tariffs</h1>
              <p className="text-sm text-gray-500">Manage parking products and site-specific pricing</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button 
              onClick={seedDefaults} 
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Create default products"
              data-testid="seed-btn"
            >
              Seed Defaults
            </button>
            <button 
              onClick={openCreateModal} 
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700" 
              data-testid="create-product-btn"
            >
              <Plus className="w-4 h-4" /> New Product
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab("products")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === "products" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-products"
          >
            <Package className="w-4 h-4" /> Products
            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100">{products.length}</span>
          </button>
          <button
            onClick={() => setActiveTab("tariffs")}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === "tariffs" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-tariffs"
          >
            <Tag className="w-4 h-4" /> Site Tariffs
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="product-search"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="type-filter"
          >
            <option value="">All Types</option>
            {PRODUCT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show inactive
          </label>
          <button onClick={loadData} className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Products Tab */}
            {activeTab === "products" && (
              <>
                {filteredProducts.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                    <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 mb-4">No products found</p>
                    <div className="flex justify-center gap-3">
                      <button onClick={seedDefaults} className="text-blue-600 hover:underline">
                        Seed default products
                      </button>
                      <span className="text-gray-300">or</span>
                      <button onClick={openCreateModal} className="text-blue-600 hover:underline">
                        Create new product
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(groupedProducts).map(([type, typeProducts]) => {
                      const typeConfig = getTypeConfig(type);
                      return (
                        <div key={type}>
                          <div className="flex items-center gap-2 mb-3">
                            <h3 className="text-sm font-semibold text-gray-700">{typeConfig.label}</h3>
                            <span className="text-xs text-gray-400">{typeProducts.length} products</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {typeProducts.map(product => (
                              <div 
                                key={product.product_id} 
                                className={`bg-white rounded-xl border ${product.active ? 'border-gray-200' : 'border-gray-300 bg-gray-50'} overflow-hidden`}
                                data-testid={`product-card-${product.product_id}`}
                              >
                                <div className="p-5">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1">
                                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium bg-${typeConfig.color}-100 text-${typeConfig.color}-700 mb-2`}>
                                        {typeConfig.label}
                                      </span>
                                      <h3 className={`font-semibold ${product.active ? 'text-gray-900' : 'text-gray-500'}`}>
                                        {product.name}
                                      </h3>
                                      {product.description && (
                                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description}</p>
                                      )}
                                    </div>
                                    <button 
                                      onClick={() => toggleProductActive(product)}
                                      className="p-1 hover:bg-gray-100 rounded ml-2"
                                      title={product.active ? "Deactivate" : "Activate"}
                                    >
                                      {product.active ? (
                                        <ToggleRight className="w-6 h-6 text-green-500" />
                                      ) : (
                                        <ToggleLeft className="w-6 h-6 text-gray-400" />
                                      )}
                                    </button>
                                  </div>
                                  
                                  <div className="space-y-2 text-sm">
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-500 flex items-center gap-1">
                                        <DollarSign className="w-4 h-4" /> Price
                                      </span>
                                      <span className="font-bold text-lg text-gray-900">{product.price_display}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-500 flex items-center gap-1">
                                        <Clock className="w-4 h-4" /> Validity
                                      </span>
                                      <span className="font-medium">{product.validity_value} {product.validity_unit}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-500">VAT Rate</span>
                                      <span className="font-medium">{product.effective_vat_rate}%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-500 flex items-center gap-1">
                                        <Building2 className="w-4 h-4" /> Scope
                                      </span>
                                      <span className={`font-medium ${product.site_id ? 'text-blue-600' : 'text-gray-600'}`}>
                                        {product.site_name}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Card Actions */}
                                <div className="border-t border-gray-100 p-3 bg-gray-50 flex items-center justify-end gap-1">
                                  <button 
                                    onClick={() => duplicateProduct(product)} 
                                    className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-blue-600"
                                    title="Duplicate product"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => openEditModal(product)} 
                                    className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-blue-600" 
                                    data-testid={`edit-${product.product_id}`}
                                    title="Edit product"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => handleDelete(product.product_id)} 
                                    className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-red-600" 
                                    data-testid={`delete-${product.product_id}`}
                                    title="Delete product"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Tariffs Tab */}
            {activeTab === "tariffs" && (
              <>
                {selectedSite === "all" ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                    <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 mb-2">Select a specific site to view its effective tariffs</p>
                    <p className="text-sm text-gray-400">Use the site selector above to choose a site</p>
                  </div>
                ) : tariffs ? (
                  <div className="space-y-4">
                    {/* Site Info Card */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{tariffs.site_name}</h3>
                          <p className="text-sm text-gray-500">Site VAT Rate: {tariffs.site_vat_rate || "Inherited (20%)"}%</p>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/commercial/sites`)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Edit Site Settings
                        </button>
                      </div>
                    </div>

                    {/* Tariffs List */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <h3 className="font-semibold text-gray-900">Effective Tariffs</h3>
                        <p className="text-sm text-gray-500">Prices shown include VAT inheritance</p>
                      </div>
                      {tariffs.tariffs?.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <p>No tariffs configured for this site</p>
                          <button onClick={() => setActiveTab("products")} className="text-blue-600 hover:underline mt-2">
                            Create products first
                          </button>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {tariffs.tariffs?.map(tariff => (
                            <div key={tariff.product_id} className="p-4 hover:bg-gray-50">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-gray-900">{tariff.product_name}</p>
                                    {tariff.has_site_tariff && (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                                        Site Override
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {getTypeConfig(tariff.product_type).label} • {tariff.validity_value} {tariff.validity_unit}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-lg text-gray-900">{tariff.effective_price_display}</p>
                                  <p className="text-xs text-gray-500">
                                    {tariff.has_site_tariff ? (
                                      <span className="text-blue-600">Overrides base: £{(tariff.base_price_pence/100).toFixed(2)}</span>
                                    ) : (
                                      <span>Inherited from {tariff.source || "platform"}</span>
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-400">VAT: {tariff.effective_vat_rate}%</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Backend Gaps Notice */}
                    <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-amber-800">Additional Tariff Configuration</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            The following tariff settings require backend support:
                          </p>
                          <ul className="mt-2 space-y-1 text-sm text-gray-600">
                            <li>• Site-specific tariff overrides (create tariff per site)</li>
                            <li>• Time-based tariff bands (e.g., first hour free)</li>
                            <li>• Day/time based pricing variations</li>
                            <li>• Maximum daily charge caps</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg m-4 max-h-[90vh] overflow-y-auto" data-testid="product-modal">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingProduct ? "Edit Product" : "Create Product"}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Type *</label>
                <select
                  value={formData.product_type}
                  onChange={(e) => setFormData({...formData, product_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  disabled={!!editingProduct}
                  data-testid="modal-product-type"
                >
                  {PRODUCT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {getTypeConfig(formData.product_type).description}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Hourly Parking - Standard"
                  data-testid="modal-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Optional description for internal reference"
                  data-testid="modal-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (pence) *</label>
                  <input
                    type="number"
                    value={formData.base_price_pence}
                    onChange={(e) => setFormData({...formData, base_price_pence: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="0"
                    data-testid="modal-price"
                  />
                  <p className="text-xs text-gray-500 mt-1">= £{(formData.base_price_pence / 100).toFixed(2)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Rate (%)</label>
                  <input
                    type="number"
                    value={formData.vat_rate}
                    onChange={(e) => setFormData({...formData, vat_rate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Inherit"
                    min="0"
                    max="100"
                    step="0.1"
                    data-testid="modal-vat"
                  />
                  <p className="text-xs text-gray-500 mt-1">Empty = inherit from site</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Validity Value</label>
                  <input
                    type="number"
                    value={formData.validity_value}
                    onChange={(e) => setFormData({...formData, validity_value: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    min="1"
                    data-testid="modal-validity-value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Validity Unit</label>
                  <select
                    value={formData.validity_unit}
                    onChange={(e) => setFormData({...formData, validity_unit: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="modal-validity-unit"
                  >
                    {VALIDITY_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Scope</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => setFormData({...formData, site_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="modal-site"
                >
                  <option value="">Platform Default (All Sites)</option>
                  {sites.map(s => (
                    <option key={s.site_id} value={s.site_id}>{s.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Platform default products apply to all sites unless overridden
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="rounded border-gray-300"
                  data-testid="modal-active"
                />
                <label htmlFor="active" className="text-sm text-gray-700">Product is active</label>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button 
                onClick={() => setShowModal(false)} 
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50" 
                data-testid="modal-cancel"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" 
                data-testid="modal-save"
              >
                {saving ? "Saving..." : editingProduct ? "Save Changes" : "Create Product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function CommercialProductsTariffs() {
  return (
    <CommercialProvider>
      <ProductsContent />
    </CommercialProvider>
  );
}
